import SiteLayout from "./components/layout/SiteLayout";

function PrivacyPolicy() {
  return (
    <SiteLayout navVariant="inner" footerVariant="legal">
      <main className="pt-32 pb-20 px-4 relative">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-stone-800 mb-2">Privacy Policy</h1>
          <p className="text-stone-500 mb-12">Last updated: January 2026</p>

          <div className="prose prose-stone max-w-none prose-headings:font-bold prose-a:text-brand-orange prose-a:no-underline hover:prose-a:text-brand-orange-hover">
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">Overview</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                Kyubot ("the Service"), provided by Ninefold ("we", "us"), is a Slack application
                that helps teams manage queues within Slack channels. This privacy policy explains
                what data we collect, why we collect it, and how we handle it.
              </p>
              <p className="text-stone-600 leading-relaxed">
                We are committed to collecting only the minimum data necessary to provide the
                Service and do not sell or share your data with third parties for marketing
                purposes.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">Data We Collect</h2>

              <h3 className="text-xl font-semibold text-stone-700 mt-6 mb-3">From Slack</h3>
              <p className="text-stone-600 leading-relaxed mb-4">
                When you install Kyubot to your Slack workspace, we access and store the following
                data:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2 mb-4">
                <li>
                  <strong>Workspace information:</strong> Team/workspace ID, team name, and domain
                </li>
                <li>
                  <strong>Channel information:</strong> Channel IDs where Kyubot is used
                </li>
                <li>
                  <strong>User information:</strong> Slack user IDs and display names of users who
                  interact with queues
                </li>
                <li>
                  <strong>Message content:</strong> The text of messages that are added to queues
                  (either via emoji reaction or !add command)
                </li>
                <li>
                  <strong>Message metadata:</strong> Timestamps and links to messages in queues
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-stone-700 mt-6 mb-3">Slack OAuth Scopes</h3>
              <p className="text-stone-600 leading-relaxed mb-4">
                Kyubot requests the following Slack permissions:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2 mb-4">
                <li>
                  <strong>chat:write:</strong> To post queue notifications and responses
                </li>
                <li>
                  <strong>channels:history, groups:history:</strong> To read message content when
                  you add items to the queue via emoji reaction
                </li>
                <li>
                  <strong>reactions:read, reactions:write:</strong> To detect queue actions and add
                  confirmation/error reactions
                </li>
                <li>
                  <strong>users:read:</strong> To display user names in queue listings
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-stone-700 mt-6 mb-3">
                For Billing (Paid Plans)
              </h3>
              <p className="text-stone-600 leading-relaxed mb-4">
                If you upgrade to a paid plan, we also store:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>
                  <strong>Stripe customer ID:</strong> To link your workspace to your billing
                  account
                </li>
                <li>
                  <strong>Subscription status:</strong> To determine your plan tier and features
                </li>
              </ul>
              <p className="text-stone-600 leading-relaxed mt-4">
                Payment processing is handled entirely by Stripe. We do not store credit card
                numbers or payment details on our servers.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">How We Use Your Data</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                We use the collected data solely to:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>Operate the queue management functionality</li>
                <li>Display queue items and user names in your Slack channels</li>
                <li>Enforce plan limits (queue count, item limits)</li>
                <li>Process billing for paid subscriptions</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">Data Storage & Security</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                Your data is stored in Cloudflare Workers KV, a globally distributed key-value
                storage system. Data is:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>Encrypted in transit (HTTPS/TLS)</li>
                <li>Stored in Cloudflare's secure infrastructure</li>
                <li>Isolated by workspace—each Slack workspace's data is stored separately</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">Data Retention</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                Queue data is retained as long as your workspace uses Kyubot:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>
                  <strong>Active queue items:</strong> Retained until marked as done or manually
                  removed
                </li>
                <li>
                  <strong>Completed items:</strong> Removed from storage immediately when marked
                  done
                </li>
                <li>
                  <strong>Workspace configuration:</strong> Retained while the app is installed
                </li>
              </ul>
              <p className="text-stone-600 leading-relaxed mt-4">
                If you uninstall Kyubot from your workspace, you may request deletion of all
                associated data by contacting us.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">Third-Party Services</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                Kyubot uses the following third-party services:
              </p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>
                  <strong>Slack:</strong> To receive events and send messages (governed by{" "}
                  <a
                    href="https://slack.com/privacy-policy"
                    className="text-brand-orange hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Slack's Privacy Policy
                  </a>
                  )
                </li>
                <li>
                  <strong>Stripe:</strong> To process payments (governed by{" "}
                  <a
                    href="https://stripe.com/privacy"
                    className="text-brand-orange hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Stripe's Privacy Policy
                  </a>
                  )
                </li>
                <li>
                  <strong>Cloudflare:</strong> To host and store data (governed by{" "}
                  <a
                    href="https://www.cloudflare.com/privacypolicy/"
                    className="text-brand-orange hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Cloudflare's Privacy Policy
                  </a>
                  )
                </li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">Your Rights</h2>
              <p className="text-stone-600 leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-stone-600 space-y-2">
                <li>
                  <strong>Access:</strong> Request a copy of the data we store about your workspace
                </li>
                <li>
                  <strong>Deletion:</strong> Request deletion of your workspace's data
                </li>
                <li>
                  <strong>Correction:</strong> Request correction of inaccurate data
                </li>
              </ul>
              <p className="text-stone-600 leading-relaxed mt-4">
                To exercise these rights, contact us at the email below.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">GDPR (European Users)</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                If you are located in the European Economic Area (EEA), you have additional rights
                under GDPR including the right to data portability and the right to lodge a
                complaint with a supervisory authority.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Our legal basis for processing your data is the performance of a contract (providing
                the Service you requested) and legitimate interests (operating and improving the
                Service).
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">Changes to This Policy</h2>
              <p className="text-stone-600 leading-relaxed">
                We may update this privacy policy from time to time. We will notify users of
                significant changes by posting a notice on our website. Continued use of the Service
                after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-800 mb-4">Contact</h2>
              <p className="text-stone-600 leading-relaxed">
                For questions about this privacy policy or to exercise your data rights, contact us
                at:{" "}
                <a href="mailto:support@kyubot.com" className="text-brand-orange hover:underline">
                  support@kyubot.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </SiteLayout>
  );
}

export default PrivacyPolicy;
